/**
 * One profile card in the profiles grid of the Listing block.
 *
 * Renders portrait, name, Foundation membership, and badges. Works with
 * catalog brains where `title` is already computed.
 * @module components/Blocks/Listing/ProfileItem
 */
import React from 'react';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import Component from '@plone/volto/components/theme/Component/Component';
import Badges from '../../Badge/Badges';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import ploneSVG from '@plone/volto/icons/plone.svg';
import './profile-item.scss';

import type { ProfileBrain } from '../../../types';

interface ProfileItemProps {
  /** The brain the Listing block's query returned. */
  item: ProfileBrain;
  /** True while the block is being edited, when links must not navigate. */
  isEditMode?: boolean;
}

const ProfileItem: React.FC<ProfileItemProps> = ({ item, isEditMode }) => {
  // A brain indexed before the column existed has no `workflow_states`.
  const reviewStates = (item.workflow_states ?? []).map((state) =>
    state.replace('|', '-'),
  );
  const isFoundationMember =
    reviewStates.filter(
      (state) => state.indexOf('foundation_member_workflow') === 0,
    ).length > 0;
  return (
    <div
      className={['userProfileItem', 'grid-item', ...reviewStates].join(' ')}
      key={item['@id']}
    >
      <ConditionalLink item={item} condition={!isEditMode}>
        <div className="gridImage">
          {/* `dependencies` is optional at runtime but typed as
                    required; an empty list behaves like omitting it. */}
          <Component
            componentName="PreviewImage"
            dependencies={[]}
            item={item}
            alt=""
            className="profileImage"
          />
        </div>
        <div className="grid-header">
          <h3 className="profile-title">{item.title || item.id}</h3>
          {isFoundationMember && (
            <span className="foundation-member-badge">
              <Icon name={ploneSVG} size="24px" />
            </span>
          )}
        </div>
        <div className="grid-body">
          <Badges items={item.badges} />
        </div>
      </ConditionalLink>
    </div>
  );
};

export default ProfileItem;
